import { GetListParams, GetListResult, GetOneParams, GetOneResult, UpdateParams, UpdateResult } from 'react-admin';
import { getClientApi } from 'src/data-communication';
import {
  SortDirectionTypeDto,
  GetUserListQueryParamsDto,
  UserDto,
  GetUserListSortByQueryParamTypeDto,
  UpdateUserDashboardSettingsBodyDto,
} from 'src/dtos';
import { mapUserDetailsDtoToModel, mapUserDtoToModel } from 'src/mappers';
import { User, UserDetails } from 'src/models';

const clientApi = getClientApi();

export const mapGetListSortOrderToSortDirectionQueryParamTypeDto = (sort: any): SortDirectionTypeDto => {
  return sort?.order ? sort.order.toLowerCase() : SortDirectionTypeDto.Asc;
};

export const mapGetListParamsToQueryParamsDto = (params: GetListParams): GetUserListQueryParamsDto => {
  const { pagination, sort, filter } = params;

  return {
    skip: pagination.perPage && pagination.perPage * (pagination.page - 1),
    take: pagination.perPage,
    sortBy: sort.field as GetUserListSortByQueryParamTypeDto,
    sortDirection: sort.order?.toLowerCase() as SortDirectionTypeDto,
    email: filter?.email,
    firstName: filter?.firstName,
    lastName: filter?.lastName,
    phoneNumber: filter?.phoneNumber,
    hasAccounts: filter?.hasAccounts,
  };
};

export const getUserList = async (params: GetListParams): Promise<GetListResult<User>> => {
  const queryParams = mapGetListParamsToQueryParamsDto(params);
  const { data, total } = await clientApi.users.list({
    queryParams,
  });

  return {
    data: data.map(mapUserDtoToModel),
    total,
  };
};

export const getUser = async (params: GetOneParams<User>): Promise<GetOneResult<UserDetails>> => {
  const { id } = params;
  const data = await clientApi.users.retrieve({
    params: { id },
  });

  return {
    data: mapUserDetailsDtoToModel(data),
  };
};

export const lockUser = async (params: UpdateParams): Promise<UpdateResult<{ id: string }>> => {
  const { id } = params;

  await clientApi.users.lock({ params: { id: id.toString() } });

  return {
    data: { id: id.toString() },
  };
};

export const unlockUser = async (params: UpdateParams): Promise<UpdateResult<{ id: string }>> => {
  const { id } = params;

  await clientApi.users.unlock({ params: { id: id.toString() } });

  return {
    data: { id: id.toString() },
  };
};

export const closeUser = async (params: UpdateParams): Promise<UpdateResult<{ id: string }>> => {
  const { id } = params;

  await clientApi.users.close({ params: { id: id.toString() } });

  return {
    data: { id: id.toString() },
  };
};

export const updateUserPostcloseOffers = async (params: UpdateParams): Promise<UpdateResult<UserDto>> => {
  const { id, data } = params;
  const { offerIds } = data;

  const result = await clientApi.users.updatePostcloseOffers({
    params: {
      id: id.toString(),
    },
    body: {
      offerIds,
    },
  });

  return {
    data: result,
  };
};

export const updateUserMfaPreferences = async (params: UpdateParams): Promise<UpdateResult<{ id: string }>> => {
  const { id } = params;
  const isEnabled = params.data?.isEnabled;

  await clientApi.users.updateUserMfaPreferences({
    queryParams: { id: id.toString() },
    body: { isEnabled },
  });

  return {
    data: { id: id.toString() },
  };
};

export const forgotPassword = async (params: UpdateParams): Promise<UpdateResult<{ id: string }>> => {
  const { id } = params;
  const { email } = params.data;

  await clientApi.users.forgotPassword({
    body: { email },
  });

  return {
    data: { id: id.toString() },
  };
};

export const updateUserDashboardSettings = async (
  params: UpdateParams<UpdateUserDashboardSettingsBodyDto & { id: string }>,
): Promise<UpdateResult<UserDetails>> => {
  const { data } = params;

  const result = await clientApi.users.updateDashboardSettings({
    body: data as UpdateUserDashboardSettingsBodyDto,
  });

  return {
    data: mapUserDetailsDtoToModel(result),
  };
};
